import API from "../../rest/api";
import {
  Debt,
  debtDocumentType,
  debtErrorMessage,
  debtType,
} from "../../interfaces/interfaces";

type DebtApiResponse<T> = T & {
  success: boolean;
  message?: string;
};

// The debt controller reports validation failures as 200 + success: false,
// so both transport errors and those need turning into a thrown Error.
// The user only ever sees our own fallback wording; the real error is logged.
const request = async <T>(
  fallbackMessage: string,
  call: () => Promise<{ data: DebtApiResponse<T> }>
): Promise<DebtApiResponse<T>> => {
  let data: DebtApiResponse<T>;
  try {
    data = (await call()).data;
  } catch (error) {
    console.error(fallbackMessage, error);
    throw new Error(fallbackMessage, { cause: error });
  }
  if (!data?.success) {
    console.error(fallbackMessage, data?.message);
    throw new Error(fallbackMessage);
  }
  return data;
};

type UploadSanctionLetterInput = {
  file: File;
  debtType: debtType;
};

export type DebtDetailsInput = {
  currentOutstanding?: number;
  // YYYY-MM-DD; the backend only accepts it for TERM debts
  actualStartDate?: string;
  // switches the loan type; the debt, its files and details are all kept
  debtType?: debtType;
};

// Every flow (underwriting today, pulse later) exposes the same debt routes
// under its own base path, e.g. "/underwriting/debt".
export const createDebtApi = (basePath: string) => {
  // Creates a new debt from its sanction letter. The backend stores the file,
  // kicks off OCR, and returns the debt with its document in PROCESSING state;
  // the result arrives later over the DEBT_* socket events.
  const uploadSanctionLetter = async ({
    file,
    debtType,
  }: UploadSanctionLetterInput): Promise<Debt> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("isNewDebt", "true");
    formData.append("debtType", debtType);
    formData.append("documentType", debtDocumentType.sanctionLetter);

    const data = await request<{ debt?: Debt }>(
      debtErrorMessage.sanctionLetterUpload,
      () =>
        API.post(basePath, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
    );
    if (!data.debt) {
      throw new Error(debtErrorMessage.sanctionLetterUpload);
    }
    return data.debt;
  };

  // Attaches a repayment schedule to an existing debt. The backend links it to
  // the sanction letter's OCR result and reports progress over the socket.
  const uploadRepaymentSchedule = async (
    debtId: string,
    file: File
  ): Promise<Debt> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("isNewDebt", "false");
    formData.append("debtId", debtId);
    formData.append("documentType", debtDocumentType.repaymentSchedule);

    const data = await request<{ debt?: Debt }>(
      debtErrorMessage.repaymentScheduleUpload,
      () =>
        API.post(basePath, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
    );
    if (!data.debt) {
      throw new Error(debtErrorMessage.repaymentScheduleUpload);
    }
    return data.debt;
  };

  // Saves the user-entered details on an existing debt. Same route as upload,
  // sent without a file, which the backend treats as a details update.
  const updateDebtDetails = async (
    debtId: string,
    details: DebtDetailsInput
  ): Promise<Debt> => {
    const formData = new FormData();
    formData.append("isNewDebt", "false");
    formData.append("debtId", debtId);
    if (details.currentOutstanding !== undefined) {
      formData.append("currentOutstanding", String(details.currentOutstanding));
    }
    if (details.actualStartDate !== undefined) {
      formData.append("actualStartDate", details.actualStartDate);
    }
    if (details.debtType !== undefined) {
      formData.append("debtType", details.debtType);
    }

    const data = await request<{ debt?: Debt }>(
      debtErrorMessage.detailsSave,
      () =>
        API.post(basePath, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
    );
    if (!data.debt) {
      throw new Error(debtErrorMessage.detailsSave);
    }
    return data.debt;
  };

  // Removes the whole debt and every file on it. This is the only way to drop a
  // sanction letter, since the backend refuses to delete that document alone.
  const deleteDebt = (debtId: string) =>
    request<{ debtId: string }>(debtErrorMessage.debtRemove, () =>
      API.delete(`${basePath}/${debtId}`)
    );

  // Removes one non-sanction-letter file and returns the debt without it.
  const deleteDebtDocument = async (
    debtId: string,
    documentId: number
  ): Promise<Debt> => {
    const data = await request<{ debt?: Debt }>(
      debtErrorMessage.fileRemove,
      () => API.delete(`${basePath}/${debtId}/documents/${documentId}`)
    );
    if (!data.debt) {
      throw new Error(debtErrorMessage.fileRemove);
    }
    return data.debt;
  };

  // The server's current copy of every debt on this form. Used to catch up on
  // socket results that happened while nothing was listening.
  const getDebts = async (): Promise<Debt[]> => {
    const data = await request<{ debts?: Debt[] }>(debtErrorMessage.debtsLoad, () =>
      API.get(basePath)
    );
    return data.debts || [];
  };

  return {
    getDebts,
    uploadSanctionLetter,
    uploadRepaymentSchedule,
    updateDebtDetails,
    deleteDebt,
    deleteDebtDocument,
  };
};

export type DebtApi = ReturnType<typeof createDebtApi>;
